import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useDocumentDirection } from '@/hooks/useDocumentDirection';
import { cn } from '@/lib/utils';
import NavFlyout, { FlyoutRowProps } from './NavFlyout';
import { activePathIn, isPathActive, NavItem, NavModel, NavNode } from './nav-model';

/**
 * Nav row anatomy: 36px rows, 13px medium text, muted text at rest, a hover
 * surface, and a subtle active state — a soft brand fill and brand text.
 * No section headings, no divider rules, no start-edge bar.
 */
const rowBase =
    'relative flex items-center gap-2.5 rounded-md text-[13px] font-medium no-underline outline-none transition-colors duration-100 focus-visible:ring-2 focus-visible:ring-ring';
const rowIdle = 'text-muted-foreground hover:bg-surface-hover hover:text-foreground';
const rowActive = 'bg-primary/10 text-primary';

function NavIcon({ icon: Icon }: { icon: NavItem['icon'] }) {
    return <Icon aria-hidden="true" className="size-4 shrink-0" />;
}

const nodeIsActive = (node: NavNode, pathname: string) =>
    [node.item, ...node.children].some(entry => isPathActive(entry.path, pathname));

export default function MenuContent({
    model,
    collapsed = false,
    variant = 'sidebar',
    onNavigate,
}: {
    model: NavModel;
    /** Desktop collapsed rail: icons only, labels in tooltips / flyouts. */
    collapsed?: boolean;
    /** "sidebar" opens related pages in flyouts; "drawer" lists them inline. */
    variant?: 'sidebar' | 'drawer';
    /** Called on every navigation (the mobile drawer closes itself). */
    onNavigate?: () => void;
}) {
    const { t } = useTranslation('crm');
    const { pathname } = useLocation();
    const dir = useDocumentDirection();
    const side = dir === 'rtl' ? 'left' : 'right';
    const Chevron = dir === 'rtl' ? ChevronLeft : ChevronRight;
    const isDrawer = variant === 'drawer';
    const iconOnly = collapsed && !isDrawer;
    const label = (entry: NavItem) => t(entry.labelKey, entry.label);

    const leafRow = (entry: NavItem, active: boolean) => {
        if (iconOnly) {
            return (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Link
                            to={entry.path}
                            onClick={onNavigate}
                            aria-label={label(entry)}
                            aria-current={active ? 'page' : undefined}
                            className={cn(
                                rowBase,
                                'mx-auto h-9 w-9 justify-center',
                                active ? rowActive : rowIdle
                            )}
                        >
                            <NavIcon icon={entry.icon} />
                        </Link>
                    </TooltipTrigger>
                    <TooltipContent side={side} className="text-xs">
                        {label(entry)}
                    </TooltipContent>
                </Tooltip>
            );
        }
        return (
            <Link
                to={entry.path}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
                className={cn(rowBase, 'h-9 px-2.5', active ? rowActive : rowIdle)}
            >
                <NavIcon icon={entry.icon} />
                <span className="min-w-0 flex-grow truncate text-start">{label(entry)}</span>
            </Link>
        );
    };

    const flyoutRow = (node: NavNode, active: boolean) => (
        <NavFlyout
            route={node.item}
            items={node.children}
            pathname={pathname}
            side={side}
            onNavigate={onNavigate}
        >
            {({ open, rowRef, linkProps }: FlyoutRowProps) => (
                <Link
                    ref={rowRef}
                    to={node.item.path}
                    onClick={event => {
                        linkProps.onClick(event);
                        if (!event.defaultPrevented) onNavigate?.();
                    }}
                    onKeyDown={linkProps.onKeyDown}
                    onPointerDown={linkProps.onPointerDown}
                    aria-haspopup={linkProps['aria-haspopup']}
                    aria-expanded={linkProps['aria-expanded']}
                    aria-label={iconOnly ? label(node.item) : undefined}
                    aria-current={isPathActive(node.item.path, pathname) ? 'page' : undefined}
                    className={cn(
                        rowBase,
                        iconOnly ? 'mx-auto h-9 w-9 justify-center' : 'h-9 px-2.5',
                        active ? rowActive : rowIdle,
                        open && !active && 'bg-surface-hover text-foreground'
                    )}
                >
                    <NavIcon icon={node.item.icon} />
                    {!iconOnly && (
                        <>
                            <span className="min-w-0 flex-grow truncate text-start">
                                {label(node.item)}
                            </span>
                            <Chevron aria-hidden="true" className="size-3.5 shrink-0 opacity-60" />
                        </>
                    )}
                </Link>
            )}
        </NavFlyout>
    );

    // Drawer: parent row, then its related pages indented on a guide line.
    const inlineRow = (node: NavNode, active: boolean) => {
        const activeChild = activePathIn(
            node.children.map(child => child.path),
            pathname
        );
        return (
            <>
                {leafRow(node.item, active && !activeChild)}
                <ul className="m-0 ms-[1.125rem] mt-0.5 list-none space-y-0.5 border-s border-border-subtle p-0 ps-2">
                    {node.children.map(child => {
                        const childActive = child.path === activeChild;
                        return (
                            <li key={child.path}>
                                <Link
                                    to={child.path}
                                    onClick={onNavigate}
                                    aria-current={childActive ? 'page' : undefined}
                                    className={cn(
                                        rowBase,
                                        'h-8 px-2.5',
                                        childActive ? rowActive : rowIdle
                                    )}
                                >
                                    <span className="min-w-0 flex-grow truncate text-start">
                                        {label(child)}
                                    </span>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </>
        );
    };

    const renderNodes = (nodes: NavNode[]) => (
        <ul className="m-0 list-none space-y-0.5 p-0">
            {nodes.map(node => {
                const active = nodeIsActive(node, pathname);
                let row: React.ReactNode;
                if (node.children.length === 0) row = leafRow(node.item, active);
                else if (isDrawer) row = inlineRow(node, active);
                else row = flyoutRow(node, active);
                return <li key={node.item.path}>{row}</li>;
            })}
        </ul>
    );

    return (
        <TooltipProvider delayDuration={350}>
            <div className="flex h-full flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-3">
                    {renderNodes(model.main)}
                </div>
                {/* Administration, pinned to the bottom outside the scroll. */}
                {model.bottom.length > 0 && (
                    <div className="shrink-0 px-2 pb-3 pt-2">{renderNodes(model.bottom)}</div>
                )}
            </div>
        </TooltipProvider>
    );
}
